<template>
  <div class="income-statement">
    <NavBar>
      <template #left>
        <BackButton @click="onBack" />
      </template>
    </NavBar>
    <main class="income-statement__main">
      <HeadingDisplay
        title="Informe de rendimentos"
        subtitle="Visualize seus Informes de Rendimentos disponíveis."
      />
      <ContextBanner
        class="income-statement__info-alert"
        title="O Informe reúne dados de 2 fontes pagadoras: Warren Corretora e Renascença DTVM. Confira o documento completo antes de declarar seu IR."
        type="info"
        icon="EF0080"
      /> 
      <LoaderIndicator v-if="isLoading" with-container />
      <div v-else-if="hasError || isEmpty" class="income-statement__feedback">
        <HelperViewContextual
          v-bind="helperViewProps"
          @click="fetchIncomeReportYears"
        />
      </div>
      <div
        v-for="(yearDetails, index) in incomeReportYears"
        v-else
        :key="index"
        class="income-statement__year-container"
      >
        <BaseText
          class="income-statement__year-container--title"
          size="p2"
          :color="NebraskaColors.elementPrimary"
        >
          {{ yearDetails.year }}
        </BaseText>
        <Row
          v-for="(reportDetails, reportIndex) in yearDetails.reports"
          :key="reportIndex"
          has-interaction
          @click="onClickDownload(yearDetails.year, reportDetails.type.value)"
        >
          <template #top-left>
            {{ reportDetails.type.formatted }}
          </template>
          <template #top-right>
            <SpinnerLoader
              v-if="reportDetails.isLoading"
              :size="15"
              :color="NebraskaColors.elementSecondary"
            />
            <BaseIcon
              v-else
              icon="EA0230"
              aria-label="Download"
              :colors="NebraskaColors.elementSecondary"
            />
          </template>
        </Row>
      </div>
    </main>
  </div>
</template>

<script lang="ts" setup>
import { computed, onMounted, ref } from 'vue';
import { useRouter } from 'vue-router/composables';
import { notify } from '@/modules/common/helpers/notification';
import { NotificationTypes } from '@/modules/common/services/http/notification';

import { NavBar, HeadingDisplay, LoaderIndicator, HelperViewContextual, BaseText, Row, SpinnerLoader, BaseIcon } from '@warrenbrasil/nebraska-web';
import { NebraskaColors } from '@warrenbrasil/nebraska-tokens-web';
import { BackButton } from '@/modules/common/components/buttons/BackButton';
import ContextBanner from '@/modules/common/components/ContextBanner/ContextBanner.vue';

import {
  getIncomeReportYears,
  getIncomeReport,
  IIncomeReportYears
} from '@/modules/investments/services/investments';
import { forceFileDownload } from '@/modules/common/helpers/force-download';

const router = useRouter();

const hasError = ref(false);
const isLoading = ref(false);
const incomeReportYears = ref<IIncomeReportYears[]>([]);

function onBack() {
  router.push({ name: 'home' });
}

const isEmpty = computed(() => {
  return Boolean(incomeReportYears.value.length === 0);
});

const helperViewProps = computed(() => {
  const errorProps = {
    'generic-error': true,
    buttonLabel: 'Tentar novamente'
  };

  const emptyProps = {
    icon: 'ED0030',
    description: 'Você não possui informes de rendimentos',
    context: 'neutral'
  };

  return hasError.value ? errorProps : emptyProps;
});

async function fetchIncomeReportYears() {
  try {
    hasError.value = false;
    isLoading.value = true;

    const data = await getIncomeReportYears();

    incomeReportYears.value = data;
  } catch {
    hasError.value = true;
  } finally {
    isLoading.value = false;
  }
}

async function onClickDownload(year: number, type: string) {
  try {
    toggleItemLoading(year, type);

    const report = await getIncomeReport(year, type);

    notify({
      group: 'wrn-notifications',
      text: 'Download iniciado',
      type: NotificationTypes.success
    });

    forceFileDownload({
      data: report,
      type: 'application/pdf',
      fileName: `${type}-${year}.pdf`
    });
  } catch {
    notify({
      group: 'wrn-notifications',
      text: 'Houve um problema ao iniciar o download',
      type: NotificationTypes.error
    });
  } finally {
    toggleItemLoading(year, type);
  }
}

function toggleItemLoading(year: number, type: string) {
  const reportYearIndex = incomeReportYears.value.findIndex(
    detail => detail.year === year
  );

  const reportIndex = incomeReportYears.value[
    reportYearIndex
  ].reports.findIndex(report => report.type.value === type);

  incomeReportYears.value[reportYearIndex].reports[reportIndex].isLoading =
    !incomeReportYears.value[reportYearIndex].reports[reportIndex].isLoading;
}

onMounted(() => {
  fetchIncomeReportYears();
});
</script>

<style lang="less" scoped>
.income-statement {
  .navbar-container {
    border-bottom: none;
  }

  &__feedback {
    display: flex;
    align-items: center;
    justify-content: center;
  }

  &__main {
    max-width: 1286px;
    margin: 0 auto;
    padding: @size-spacing-x900 @size-spacing-x450;

    .heading-display {
      margin-bottom: @size-spacing-x600;
    }
  }

  &__info-alert {
    margin-bottom: @size-spacing-x800;
  }

  &__year-container {
    margin-bottom: @size-spacing-x500;

    &--title {
      font-weight: bold;
      margin-bottom: @size-spacing-x350;
    }
  }
}
</style>
